import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { IconButton } from "../UI";
import closeImg from "../asserts/img/close_white.svg";
import { b64toBlob } from "../utils/helper";
import { subscribe, unsubscribe } from "../utils/events";
import { IMAGE_SUFFIXES, OPEN_ATTACHMENT_EVENT } from "../utils/attachmentViewer";
import { attachmentDisplayName } from "../utils/useAttachmentUploads";

const Overlay = styled("div")(() => ({
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  // Above modals (1102), below error toasts
  zIndex: 1300,
  display: "flex",
  flexDirection: "column",
  backgroundColor: "rgba(20, 20, 20, 0.94)",
}));

const TopBar = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: 16,
  minHeight: 56,
  padding: "0 16px",
  color: "#ffffff",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
}));

const FileName = styled("h2")(() => ({
  flex: 1,
  minWidth: 0,
  margin: 0,
  fontSize: 16,
  fontWeight: 500,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "start",
  unicodeBidi: "plaintext",
}));

const Content = styled("div")(() => ({
  flex: 1,
  minHeight: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 12,
}));

const TextButton = styled("button")(({ $primary }) => ({
  minHeight: 44,
  padding: "0 20px",
  borderRadius: 22,
  border: "1px solid #ffffff",
  backgroundColor: $primary ? "#ffffff" : "transparent",
  color: $primary ? "#1655B9" : "#ffffff",
  fontFamily: "inherit",
  fontSize: 15,
  fontWeight: 500,
  cursor: "pointer",
}));

const DownloadIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3v12m0 0l-5-5m5 5l5-5M4 19h16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const getFileKind = (suffix, mimeType) => {
  const normalizedSuffix = (suffix || "").toLowerCase();
  if (IMAGE_SUFFIXES.includes(normalizedSuffix) || mimeType?.startsWith("image/")) return "image";
  if (normalizedSuffix === "pdf" || mimeType === "application/pdf") return "pdf";
  return "other";
};

// Desktop browsers show PDFs inline. Phones don't reliably (iOS shows only the first page,
// Android Chrome shows nothing), so there the PDF is opened in the device's own viewer instead
const canShowPdfInline = () =>
  navigator.pdfViewerEnabled !== false &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const downloadFile = (url, fileName) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// A data: URL (the attachment's base64) becomes a blob URL; plain URLs are used as they are
const toViewableUrl = (source) => {
  if (!source.startsWith("data:")) return { url: source, mimeType: undefined, isBlob: false };
  const blob = b64toBlob(source);
  return { url: URL.createObjectURL(blob), mimeType: blob.type, isBlob: true };
};

// In-portal viewer for attachments (images and PDFs); other file types are downloaded.
// Opened from anywhere with openAttachment() (utils/attachmentViewer.js).
const AttachmentViewer = () => {
  const { t } = useTranslation("common");
  const [file, setFile] = useState(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const handleOpen = (e) => {
      const { source, filename, suffix } = e.detail || {};
      try {
        const viewable = toViewableUrl(source);
        const kind = getFileKind(suffix, viewable.mimeType);
        const name = attachmentDisplayName({ EXTFILEDES: filename || "" }, suffix);

        if (kind === "other") {
          downloadFile(viewable.url, name);
          if (viewable.isBlob) setTimeout(() => URL.revokeObjectURL(viewable.url), 1000);
          return;
        }
        setFile({ ...viewable, kind, name });
      } catch (err) {
        console.error("Failed to open attachment", filename, err);
      }
    };

    subscribe(OPEN_ATTACHMENT_EVENT, handleOpen);
    return () => unsubscribe(OPEN_ATTACHMENT_EVENT, handleOpen);
  }, []);

  // Free the blob URL when the viewer closes or shows another file
  useEffect(() => {
    if (!file?.isBlob) return undefined;
    return () => URL.revokeObjectURL(file.url);
  }, [file]);

  useEffect(() => {
    if (!file) return undefined;
    // Move focus into the dialog so keyboard and screen-reader users land in it
    overlayRef.current?.focus();
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setFile(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [file]);

  if (!file) return null;

  const close = () => setFile(null);
  const download = () => downloadFile(file.url, file.name);
  const showPdfInline = file.kind === "pdf" && canShowPdfInline();

  return (
    <Overlay
      ref={overlayRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={file.name}
      style={{ outline: "none" }}
    >
      <TopBar>
        <FileName title={file.name}>{file.name}</FileName>
        <button
          type="button"
          onClick={download}
          aria-label={t("download")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 44,
            minHeight: 44,
            border: "none",
            background: "transparent",
            color: "#ffffff",
            cursor: "pointer",
          }}
        >
          <DownloadIcon />
        </button>
        <IconButton src={closeImg} iconSize={16} label={t("close")} onClick={close} />
      </TopBar>
      <Content
        onClick={(e) => {
          // Tapping the dark area around the file closes the viewer
          if (e.target === e.currentTarget) close();
        }}
      >
        {file.kind === "image" && (
          <img
            src={file.url}
            alt={file.name}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        )}
        {showPdfInline && (
          <iframe
            src={file.url}
            title={file.name}
            style={{ width: "100%", height: "100%", border: 0, backgroundColor: "#ffffff" }}
          />
        )}
        {file.kind === "pdf" && !showPdfInline && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
            <p style={{ color: "#ffffff", margin: 0, fontSize: 15 }}>{t("pdf_open_hint")}</p>
            <div style={{ display: "flex", gap: 12 }}>
              <TextButton type="button" $primary onClick={() => window.open(file.url, "_blank")}>
                {t("open_file")}
              </TextButton>
              <TextButton type="button" onClick={download}>
                {t("download")}
              </TextButton>
            </div>
          </div>
        )}
      </Content>
    </Overlay>
  );
};

export default AttachmentViewer;
